import { createApp } from "vue";
import "./style.css";
import "./styles/forms.scss";
import { createRouter, createWebHistory } from "vue-router";

import { library } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import {
  faPaperclip,
  faPen,
  faPlus,
  faQrcode,
  faTv,
  faCheck,
  faX,
  faPrint,
  faSpinner,
  faFloppyDisk,
  faFileArrowUp,
  faHouse,
  faPerson,
  faCaretUp,
  faMagnifyingGlass,
  faCopy,
  faPaperPlane,
  faLink,
  faPhotoFilm,
  faImage,
  faVideo,
  faFileSignature,
  faDoorOpen,
  faRightFromBracket,
  faImages,
  faEye,
  faEyeSlash,
  faRotateLeft,
} from "@fortawesome/free-solid-svg-icons";

import VueDatePicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";

import App from "./App.vue";

import VetllaFormView from "./views/VetllaFormView.vue";
import VetllaLinksView from "./views/VetllaLinksView.vue";
import QrView from "./views/QrView.vue";
import DiapositivasView from "./views/DiapositivasView.vue";
import BifurcacionView from "./views/BifurcacionView.vue";
import UploadView from "./views/UploadView.vue";
import PresentationView from "./views/PresentationView.vue";
import PresentationSalaView from "./views/PresentationSalaView.vue";
import ListVetllasView from "./views/ListVetllasView.vue";
import SalasView from "./views/SalasView.vue";
import ExpiredView from "./views/ExpiredView.vue";
import LoginView from "./views/LoginView.vue";
import NotFoundView from "./views/NotFoundView.vue";
import useErpApiClient from "./erp-api-client";
import UploadViewTest from "./views/UploadViewTest.vue";

const erpClient = useErpApiClient();
const checkSession = (_to: any, _from: any, next: any) => {
  erpClient.checkSession().then((isValid) => {
    if (!isValid) {
      next({ name: "login" });
    } else {
      next();
    }
  });
};

const routes = [
  { path: "/", redirect: "/sales", name: "home" },
  {
    path: "/bifurcacion",
    component: BifurcacionView,
    name: "bifurcacion",
    beforeEnter: [checkSession],
  },
  {
    path: "/vetlla/create",
    component: VetllaFormView,
    name: "vetllaCreate",
    beforeEnter: [checkSession],
  },
  {
    path: "/vetlla/edit/:idVetlla",
    component: VetllaFormView,
    props: true,
    name: "vetllaEdit",
    beforeEnter: [checkSession],
  },
  {
    path: "/qr/vetlla/:idVetlla",
    component: QrView,
    name: "qrVetlla",
    props: (route: any) => ({ id: route.params.idVetlla, qrType: "vetlla" }),
  },
  // {
  //   path: "/qr/sala/:idSala",
  //   component: QrView,
  //   name: "qrSala",
  //   props: (route: any) => ({ id: route.params.idSala, qrType: "sala" }),
  // },
  // {
  //   path: "/edit/:idVetlla",
  //   component: EditView,
  //   props: true,
  //   name: "edit",
  // },
  {
    path: "/diapositivas/:idVetlla",
    component: DiapositivasView,
    props: true,
    name: "diapositivas",
    meta: {
      showNavbar: false,
    },
  },
  {
    path: "/upload/vetlla/:id",
    component: UploadView,
    props: true,
    meta: {
      showNavbar: false,
      uploadType: "vetlla",
    },
  },
  {
    path: "/upload/vetlla/test/:id",
    component: UploadViewTest,
    props: true,
    meta: {
      showNavbar: false,
      uploadType: "vetlla",
    },
  },
  {
    path: "/vetlla/links/:idVetlla",
    component: VetllaLinksView,
    name: "vetllaLinks",
    props: true,
    beforeEnter: [checkSession],
  },
  // {
  //   path: "/upload/sala/:id",
  //   component: UploadView,
  //   props: true,
  //   meta: {
  //     showNavbar: false,
  //     uploadType: "sala",
  //   },
  // },
  // @deprecated Reemplazada per /presentation/sala/:idSala (PresentationSalaView.vue).
  // Sense enllaços actius. Es manté per compatibilitat.
  {
    path: "/presentation/:idVetlla",
    component: PresentationView,
    props: true,
    name: "presentation",
    meta: {
      isFullHeight: true,
      showNavbar: false,
    },
  },
  {
    path: "/presentation/sala/:idSala",
    component: PresentationSalaView,
    props: true,
    name: "presentationSala",
    meta: {
      isFullHeight: true,
      showNavbar: false,
    },
  },
  {
    path: "/vetlles",
    component: ListVetllasView,
    name: "vetlles",
    beforeEnter: [checkSession],
  },
  {
    path: "/sales",
    component: SalasView,
    name: "sales",
    beforeEnter: [checkSession],
  },
  {
    path: "/expired",
    component: ExpiredView,
    name: "expired",
    meta: {
      showNavbar: false,
    },
  },
  { path: "/login", component: LoginView, name: "login" },
  { path: "/:pathMatch(.*)*", component: NotFoundView },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

library.add(faPaperclip);
library.add(faPen);
library.add(faQrcode);
library.add(faTv);
library.add(faPlus);
library.add(faCheck);
library.add(faX);
library.add(faPrint);
library.add(faSpinner);
library.add(faFloppyDisk);
library.add(faFileArrowUp);
library.add(faPerson);
library.add(faHouse);
library.add(faCaretUp);
library.add(faMagnifyingGlass);
library.add(faCopy);
library.add(faPaperPlane);
library.add(faLink);
library.add(faPhotoFilm);
library.add(faImage);
library.add(faVideo);
library.add(faFileSignature);
library.add(faDoorOpen);
library.add(faRightFromBracket);
library.add(faImages);
library.add(faEye);
library.add(faEyeSlash);
library.add(faRotateLeft);

function sendErrorToServer(payload: Record<string, unknown>) {
  fetch("/api/log-error", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }).catch(() => {});
}

const app = createApp(App);

app
  .component("font-awesome-icon", FontAwesomeIcon)
  .component("VueDatePicker", VueDatePicker)
  .use(router);

app.config.errorHandler = (err, _instance, info) => {
  console.error(err);
  sendErrorToServer({
    source: "vue",
    message: err instanceof Error ? err.message : String(err),
    stack: err instanceof Error ? err.stack : String(err),
    info: info,
    url: window.location.href,
    timestamp: new Date().toISOString(),
  });
};

// addEventListener no suprime el error: el navegador lo muestra en su
// formato nativo (con source maps resueltos si están disponibles)
window.addEventListener("error", (event) => {
  const stack = event.error?.stack || event.message;
  sendErrorToServer({
    source: "window",
    message: event.message,
    stack: stack || "",
    file: event.filename,
    line: event.lineno,
    col: event.colno,
    url: window.location.href,
    timestamp: new Date().toISOString(),
  });
});

window.addEventListener("unhandledrejection", (event) => {
  const reason = event.reason;
  sendErrorToServer({
    source: "unhandledrejection",
    message: reason?.message || String(reason),
    stack: reason instanceof Error ? reason.stack : String(reason),
    url: window.location.href,
    timestamp: new Date().toISOString(),
  });
});

app.mount("#app");
